import { apiErrorSchema, type ErrorCode } from "@repro/shared";
import type { z } from "zod";

export const API_BASE = "/api/v1";

/** An API error with what the UI needs: code, plain message, request id, field messages. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "NETWORK",
    message: string,
    readonly requestId?: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export interface RequestOptions<T extends z.ZodType> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  /** Response shape. Checked in development builds so contract drift shows up at once. */
  schema?: T;
}

function queryString(query: RequestOptions<z.ZodType>["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, Array.isArray(value) ? value.join(",") : String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

async function toError(response: Response): Promise<ApiRequestError> {
  const body = await response.json().catch(() => undefined);
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    const { code, message, requestId, fields } = parsed.data.error;
    return new ApiRequestError(response.status, code, message, requestId, fields);
  }
  return new ApiRequestError(
    response.status,
    "INTERNAL",
    "Something went wrong. Try again in a moment.",
    response.headers.get("x-request-id") ?? undefined,
  );
}

/** The one fetch wrapper (web-app.md): cookies, JSON, typed errors. */
export async function api<T extends z.ZodType = z.ZodUnknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<z.infer<T>> {
  let response: Response;
  try {
    response = await fetch(
      `${path.startsWith("/api/") ? "" : API_BASE}${path}${queryString(options.query)}`,
      {
        method: options.method ?? "GET",
        credentials: "include",
        headers: options.body !== undefined ? { "content-type": "application/json" } : {},
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      },
    );
  } catch {
    throw new ApiRequestError(
      0,
      "NETWORK",
      "Repro cannot be reached. Check your connection and try again.",
    );
  }
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as z.infer<T>;
  const body: unknown = await response.json();
  if (options.schema && import.meta.env.DEV) return options.schema.parse(body);
  return body as z.infer<T>;
}
